import { defineStore } from "pinia";
import { JmApi } from "../jm-api/jm-api";
import { applySurchargeToResults } from "../utils/surcharge";
import { usePlzSurchargeStore } from "./plzSurchargeStore";
import { useMapOutputStore } from "./mapOutputStore";
import {
  ADRESS_URL,
  ADRESS_WITH_ID_URL,
  CREXPORT_URL,
  CRIMPORT_URL,
  GEOCODING_URL,
  OFFICE_ADRESS_ID_URL,
  PARAMETERS_URL,
  // PARAMETERS_BY_TRANSPORT_URL,
  PATHS_GEO_URL,
  DOWNLOAD_CSV_URL,
  DOWNLOAD_EXCEL_URL,
  ROUTE_ERRORS_URL,
  DEFAULT_PARAMETERS_URL,
  MOBSCORE_URL,
  OFFICE_PARAMETERS_URL,
  OFFICE_PARAMETERS_INITIALIZE_URL,
  OFFICE_PARAMETERS_DELETE_URL,
} from "../config/urls";
import {
  CleverRouteImport,
  ImportSettingsInput,
  // ImportAnalysisType,
  ImportError,
  // ImportStatus,
  StoppingStatus,
  Transport,
} from "../model/cleverRouteImport";
import { PathsView } from "../jm-data-instances/paths";

// Interface for office-specific parameters (will replace global parameters)
interface OfficeParameter {
  id?: number;
  officeId?: number;
  office_id?: number;
  transportType?: string;
  transport_type?: string;
  speed?: number;
  addedTimeStart?: number;
  added_time_start?: number;
  addedTimeParking?: number;
  added_time_parking?: number;
  addedTimeFixed?: number;
  added_time_fixed?: number;
  addedTimeRelative?: number;
  added_time_relative?: number;
  addedTimeSum?: number;
  added_time_sum?: number;
  co2PerKm?: number;
  co2_per_km?: number;
  co2Fixed?: number;
  co2_fixed?: number;
  costFixedYear?: number;
  cost_fixed_year?: number;
  costFixedMonth?: number;
  cost_fixed_month?: number;
  costFixedDay?: number;
  cost_fixed_day?: number;
  costPerKm?: number;
  cost_per_km?: number;
  sportPerKm?: number;
  sport_per_km?: number;
  rangeMinKm?: number;
  range_min_km?: number;
  rangeMaxKm?: number;
  range_max_km?: number;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
}

export const useCleverRouteImportStore = defineStore("cleverRouteImport", {
  state: () => ({
    parametersUpdated: false,
    data: {
      paths: [] as object[],
    },
    parameters: [] as object[],
    defaultParameters: [] as object[],
    // New office-specific state (will replace existing parameters)
    officeParameters: [] as OfficeParameter[],
    parametersLoading: false,
    parametersInitialized: false,
    parametersError: null as string | null,
    currentOfficeId: null as number | null, // Track which office's parameters are loaded
    current_status: 0 as number,
    dbPaths: [] as object[],
    failedGeoPaths: [] as object[],
    uniqueAddresses: [] as object[],
    imports: [],
    number_of_paths: 0,
    // Frontend-driven batch processing state
    batch_size: 5 as number, // Reduced from 10 to 5 for faster per-batch completion (~1-2 minutes)
    total_batches: 0 as number,
    completed_batches: 0 as number,
    current_batch_index: 0 as number,
    is_batch_importing: false as boolean,
    abort_requested: false as boolean,
    last_abort_summary: null as any,
    // Resume capability - persistent session tracking
    import_session_id: null as string | null,
    completed_batch_ranges: [] as Array<{
      rangeStart: number;
      rangeEnd: number;
      completedAt: string;
    }>,
    last_completed_batch: -1 as number,
    current_import_settings: null as any,
    intervalId: null as unknown as NodeJS.Timer | number | null,
    selectedTransports: [] as Transport[],
    // For tracking the stopping process of an import.
    stoppingStatus: StoppingStatus.None as StoppingStatus,
    // For tracking interaction with specific imports.
    selectedImportId: undefined as Number | undefined,
    route_errors: [] as object[],
    // Ergebnisse, wie die Oberflaeche sie sieht - je nach Schalter mit oder
    // ohne eingerechneten Stauaufschlag.
    cr_import_results: [] as object[],
    // Die unveraenderte Antwort von /cr-export. Wird aufbewahrt, damit der
    // Schalter ohne neue Abfrage umschalten kann und beim Ausschalten wieder
    // exakt die urspruenglichen Zahlen erscheinen.
    cr_import_results_raw: [] as object[],
  }),
  getters: {
    getImports(state) {
      return state.imports;
    },
    getStoppingStatus(state) {
      return state.stoppingStatus;
    },
    getSelectedImportId(state) {
      return state.selectedImportId;
    },
    // Frontend batch processing getters
    getBatchProgress(state) {
      if (state.total_batches === 0) return 0;
      return Math.round((state.completed_batches / state.total_batches) * 100);
    },
    getBatchStatus(state) {
      return {
        current_batch: state.current_batch_index + 1,
        total_batches: state.total_batches,
        completed_batches: state.completed_batches,
        is_importing: state.is_batch_importing,
        progress_percentage: Math.round(
          (state.completed_batches / state.total_batches) * 100,
        ),
      };
    },
    // New office parameter getters (designed to replace global parameter access)
    /**
     * Get all office parameters (will replace direct 'parameters' access)
     */
    getOfficeParameters: (state) => state.officeParameters,
    /**
     * Get parameter for specific transport type (enhanced version of existing logic)
     * @param {string} transportType - Type of transport (car, bike, etc.)
     */
    getParameterByTransport: (state) => (transportType: string) => {
      return state.officeParameters.find(
        (p) =>
          p.transportType === transportType ||
          p.transport_type === transportType,
      );
    },
    /**
     * Check if parameters are currently loading
     */
    isParametersLoading: (state) => state.parametersLoading,
    /**
     * Check if office has initialized parameters
     */
    areParametersInitialized: (state) => state.parametersInitialized,
    /**
     * Get parameters error message
     */
    getParametersError: (state) => state.parametersError,
    /**
     * Check if office has custom parameters (not just defaults)
     * @returns {boolean}
     */
    hasCustomParameters: (state) => {
      // If no office is selected, we don't have custom parameters
      if (!state.currentOfficeId) {
        return false;
      }
      // Check if parameters exist AND they belong to the current office
      return (
        state.officeParameters.length > 0 &&
        state.officeParameters.some(
          (p) =>
            (p.officeId && p.officeId === state.currentOfficeId) ||
            (p.office_id && p.office_id === state.currentOfficeId),
        )
      );
    },
    /**
     * Get the current office ID for which parameters are loaded
     */
    getCurrentOfficeId: (state) => state.currentOfficeId,
  },
  actions: {
    /**
     * Fetches adress data for adress with given id
     */
    async getAddressData(addressId: number) {
      try {
        // Fetch address data from the store or make an API call if necessary
        const response = await JmApi.get(
          `${ADRESS_WITH_ID_URL}?adress_id=${addressId}`,
        );
        // console.log("Received address data in getAddressData by ID:", response);

        const data = response;
        return data;
      } catch (error) {
        console.error("Error fetching address data:", JSON.stringify(error));
        throw error;
      }
    },
    /**
     * Fetches the CleverRoute-Export from jm-database.
     * NOTE: this function throws errors, that need to be caught.
     */
    async fetchCrImportResults(
      origin_id_first: number,
      origin_id_last: number,
      office_id: number,
    ) {
      try {
        // console.log("Fetching CleverRoute-Import-Results from jm-database.");
        const url = `${CREXPORT_URL}?originIdFirst=${origin_id_first}&originIdLast=${origin_id_last}&officeId=${office_id}`;
        const response = await JmApi.get(url);
        this.cr_import_results_raw = response;
        this.refreshSurchargeView();
        // console.log("CR-Import-Results: ", this.cr_import_results);
        return this.cr_import_results;
      } catch (error) {
        throw new Error(error.message);
      }
    },
    /**
     * Rechnet den Stauaufschlag in die Ergebnisse ein - oder nimmt ihn wieder
     * heraus.
     *
     * Der einzige Ort, an dem der Aufschlag auf die Ergebnisse trifft. Alles
     * Nachgelagerte haengt an `cr_import_results`: die Zeitspalten der
     * Tabellen ebenso wie die "beste Alternative", die in
     * `mapOutputStore.calculateBestAlternatives` aus genau diesen
     * `*_Zeit`-Feldern gerechnet wird. Deshalb genuegt hier eine Zeile, und
     * Karte und Tabellen koennen gar nicht auseinanderlaufen.
     */
    refreshSurchargeView() {
      const surchargeStore = usePlzSurchargeStore();
      this.cr_import_results = surchargeStore.enabled
        ? applySurchargeToResults(this.cr_import_results_raw)
        : this.cr_import_results_raw;
    },
    /**
     * Schaltet die Beruecksichtigung des Stauaufschlags um.
     *
     * Ohne neue Abfrage: die Rohantwort liegt bereits vor.
     */
    setSurchargeEnabled(enabled: boolean) {
      usePlzSurchargeStore().setEnabled(enabled);
      this.refreshSurchargeView();
      // Kartenlayer neu berechnen lassen: die Layer-Getter sind gecachte
      // computeds und wuerden sonst den alten Stand ausliefern.
      useMapOutputStore().surchargeVersion++;
    },
    /**
     * Fetches route errors from jm-database.
     * NOTE: this function throws errors, that need to be caught.
     */
    async fetchRouteErrors() {
      try {
        // console.log("Fetching route errors from jm-database.");

        // Get the first and last path IDs from dbPaths
        if (!this.dbPaths || this.dbPaths.length === 0) {
          throw new Error("No paths available to fetch route errors");
        }

        // Sort the results by path_id to ensure we get the correct first and last IDs
        const sortedResults = [...this.dbPaths].sort(
          (a, b) => a.path_id - b.path_id,
        );
        const firstPathId = sortedResults[0].path_id;
        const lastPathId = sortedResults[sortedResults.length - 1].path_id;

        // Construct the URL with the path IDs
        const url = `${ROUTE_ERRORS_URL}?path_id_first=${firstPathId}&path_id_last=${lastPathId}`;
        //console.log("Fetching route errors with URL:", url);

        const response = await JmApi.get(url);
        this.route_errors = response;
        return this.route_errors;
      } catch (error) {
        console.error("Error fetching route errors:", error);
        throw new Error(error.message);
      }
    },
    /**
     * Fetches pathview-data based on project_id, company_id and office_id from
     * jm-database.
     * NOTE: this function throws errors, that need to be caught.
     * @param project_id
     * @param company_id
     * @param office_id
     */
    async fetchPathViews(
      projectId: number,
      companyId: number,
      officeId: number,
    ) {
      try {
        console.log("Fetching Paths from jm-database.");

        const response = await JmApi.get(
          PathsView.getUrlBuilder(projectId, companyId, officeId),
        );

        this.number_of_paths = response.length;
        await this.convertPathsToUniqueAdresses(response);
        this.dbPaths = response;
        return this.dbPaths;
      } catch (error) {
        throw new Error(error.message);
      }
    },
    async fetchFailedGeocodingPaths(
      projectId: number,
      companyId: number,
      officeId: number,
    ) {
      try {
        console.log("Fetching failed geocoding paths from jm-database.");
        const get_url = `${PATHS_GEO_URL}?projectId=${projectId}&companyId=${companyId}&officeId=${officeId}`;
        const response = await JmApi.get(get_url);

        console.log("Received failed geocoding paths:", response);

        this.failedGeoPaths = response;

        return this.failedGeoPaths;
      } catch (error) {
        console.error("Error fetching failed geocoding paths:", error);
        throw new Error(error.message);
      }
    },
    /**
     * Takes the paths from the jm-database and converts them into the format of unique addresses
     */
    async convertPathsToUniqueAdresses(response: any) {
      // Create a map to store the paths by origin_id
      const pathsMap = new Map<string, object>();

      // Loop through the response and add only one path for each origin_id to the map
      response.forEach((path: any) => {
        const key = path.origin_id;
        if (!pathsMap.has(key)) {
          pathsMap.set(key, path);
        }
      });

      // Convert the map back to an array of addresses and assign it to uniqueAddresses
      this.uniqueAddresses = Array.from(pathsMap.values());

      return this.uniqueAddresses;
    },
    /**
     * Frontend-driven batch import with range parameters and resume capability.
     * Eliminates backend progress tracking and status polling.
     * @param settings (projectId, companyId, officeId, transports)
     * @param resumeSessionId Optional session ID to resume previous import
     */
    async startBatchImport(
      settings: ImportSettingsInput,
      resumeSessionId?: string,
    ) {
      console.log(
        "Starting frontend-driven batch import with settings:",
        settings,
      );

      if (!settings.projectId || !settings.companyId || !settings.officeId) {
        throw new Error(
          "Required fields projectId, companyId, and officeId must be provided",
        );
      }

      // Validate batch_size is positive
      if (!Number.isInteger(this.batch_size) || this.batch_size <= 0) {
        this.is_batch_importing = false;
        throw new Error(
          `Batch size must be a positive integer, got: ${this.batch_size}`,
        );
      }

      // Store settings for session persistence
      this.current_import_settings = settings;

      // Generate or resume session ID
      this.import_session_id =
        resumeSessionId ||
        `import_${Date.now()}_${settings.projectId}_${settings.companyId}_${settings.officeId}`;

      // Initialize batch processing state
      this.is_batch_importing = true;
      this.abort_requested = false;
      this.last_abort_summary = null;

      // Try to load existing progress if resuming
      const isResuming = await this.loadImportProgress();

      // Always refresh paths count before computing batches (critical for resume safety)
      try {
        const freshPaths = await this.fetchPathViews(
          settings.projectId,
          settings.companyId,
          settings.officeId,
        );
        this.number_of_paths = Array.isArray(freshPaths)
          ? freshPaths.length
          : this.number_of_paths;
      } catch (error) {
        console.warn(
          "Failed to refresh paths count; continuing with cached value:",
          error,
        );
        // Continue with existing number_of_paths if refresh fails
      }

      // Validate number_of_paths is positive
      if (
        !Number.isInteger(this.number_of_paths) ||
        this.number_of_paths <= 0
      ) {
        this.is_batch_importing = false;
        console.warn("No paths available to import; aborting.");
        return;
      }

      // Recalculate total batches with fresh count
      this.total_batches = Math.ceil(this.number_of_paths / this.batch_size);

      if (!isResuming) {
        // Fresh import - initialize state
        this.current_status = 0;
        this.completed_batches = 0;
        this.current_batch_index = 0;
        this.completed_batch_ranges = [];
        this.last_completed_batch = -1;

        console.log(
          `🆕 Starting fresh import: ${this.number_of_paths} paths in ${this.total_batches} batches of ${this.batch_size}`,
        );
      } else {
        // Resume existing import - clamp last_completed_batch if totals changed
        this.last_completed_batch = Math.min(
          Math.max(this.last_completed_batch ?? -1, -1),
          this.total_batches - 1,
        );
        this.current_status = this.getBatchProgress;
        console.log(
          `🔄 Resuming import: ${this.completed_batches}/${this.total_batches} batches already completed`,
        );
      }

      try {
        // Calculate which batches to process (skip completed ones)
        const startBatchIndex = this.last_completed_batch + 1;

        // Process remaining batches sequentially
        for (
          let batchIndex = startBatchIndex;
          batchIndex < this.total_batches && !this.abort_requested;
          batchIndex++
        ) {
          this.current_batch_index = batchIndex;

          const rangeStart = batchIndex * this.batch_size;

          // Guard: if rangeStart is beyond available paths, stop processing
          if (rangeStart >= this.number_of_paths) {
            console.log(
              `⚠️ Stopping batch processing: rangeStart (${rangeStart}) >= number_of_paths (${this.number_of_paths})`,
            );
            break;
          }

          const rangeEnd = Math.min(
            rangeStart + this.batch_size - 1,
            this.number_of_paths - 1,
          );

          // Guard: skip malformed ranges (shouldn't happen with correct math, but defensive)
          if (rangeEnd < rangeStart) {
            console.warn(
              `⚠️ Skipping invalid range: rangeEnd (${rangeEnd}) < rangeStart (${rangeStart})`,
            );
            continue;
          }

          console.log(
            `Processing batch ${batchIndex + 1}/${this.total_batches}: range ${rangeStart}-${rangeEnd}`,
          );

          // Send batch request with range parameters
          await this.processBatchRange(settings, rangeStart, rangeEnd);

          if (this.abort_requested) {
            break;
          }

          // Save batch completion for resume capability
          await this.saveBatchCompletion(rangeStart, rangeEnd);

          // Update progress after each completed batch (saveBatchCompletion already increments)
          this.current_status = this.getBatchProgress;
        }

        if (this.abort_requested) {
          this.is_batch_importing = false;
          throw new Error("IMPORT_ABORTED_BY_USER");
        }

        // All batches completed successfully
        this.is_batch_importing = false;
        this.current_status = 100;
        console.log("🎉 Frontend-driven batch import completed successfully!");

        // Clear completed session from storage
        await this.clearImportSession();
      } catch (error) {
        this.is_batch_importing = false;
        if (error instanceof Error && error.message === "IMPORT_ABORTED_BY_USER") {
          console.log("🛑 Batch import aborted by user");
          throw error;
        }
        console.error("❌ Batch import failed:", error);
        console.log(
          `💾 Import progress saved - can resume from batch ${this.last_completed_batch + 2}/${this.total_batches}`,
        );
        throw error;
      }
    },

    /**
     * Process a single batch range by sending request to backend
     */
    async processBatchRange(
      settings: ImportSettingsInput,
      rangeStart: number,
      rangeEnd: number,
    ) {
      // Validate range parameters before sending request
      if (
        !Number.isInteger(rangeStart) ||
        !Number.isInteger(rangeEnd) ||
        rangeStart < 0 ||
        rangeEnd < rangeStart
      ) {
        throw new Error(
          `Invalid range parameters: rangeStart=${rangeStart}, rangeEnd=${rangeEnd}. Both must be integers, rangeStart >= 0, and rangeEnd >= rangeStart.`,
        );
      }

      // URL with range parameters for backend processing
      const post_url = `${CRIMPORT_URL}?project_id=${settings.projectId}&company_id=${settings.companyId}&office_id=${settings.officeId}&rangeStart=${rangeStart}&rangeEnd=${rangeEnd}`;
      const post_body = {
        transports: settings.transports,
        importSessionId: this.import_session_id,
      };

      try {
        const response = await JmApi.post(post_url, post_body);

        // Backend returns 204 on success for range processing
        if (!response || Object.keys(response).length === 0) {
          return; // Success - empty response is expected
        }
      } catch (error) {
        console.error(
          `Error processing batch range ${rangeStart}-${rangeEnd}:`,
          error,
        );
        // The backend returns a structured Error ({ message, code, success }) e.g. on a
        // 403 quota rejection. Preserve that message instead of stringifying the object
        // to "[object Object]", so the UI can show why the import was rejected.
        const backendMessage =
          error && typeof error === "object" && "message" in error
            ? (error as { message?: string }).message
            : undefined;
        throw new Error(
          backendMessage ||
            `Failed to process batch range ${rangeStart}-${rangeEnd}: ${error}`,
        );
      }
    },

    /**
     * Save completed batch to localStorage for resume capability
     */
    async saveBatchCompletion(rangeStart: number, rangeEnd: number) {
      const completedBatch = {
        rangeStart,
        rangeEnd,
        completedAt: new Date().toISOString(),
      };

      this.completed_batch_ranges.push(completedBatch);
      this.last_completed_batch++;
      this.completed_batches++;

      // Persist to localStorage for resume capability
      const sessionKey = `cr_import_session_${this.import_session_id}`;
      const sessionData = {
        session_id: this.import_session_id,
        completed_batch_ranges: this.completed_batch_ranges,
        last_completed_batch: this.last_completed_batch,
        total_batches: this.total_batches,
        number_of_paths: this.number_of_paths,
        batch_size: this.batch_size,
        settings: this.current_import_settings,
        saved_at: new Date().toISOString(),
      };

      localStorage.setItem(sessionKey, JSON.stringify(sessionData));

      console.log(
        `✅ Batch ${this.last_completed_batch + 1}/${this.total_batches} completed and saved (range ${rangeStart}-${rangeEnd})`,
      );
    },

    /**
     * Load existing import progress for resumption
     */
    async loadImportProgress() {
      if (!this.import_session_id) return false;

      const sessionKey = `cr_import_session_${this.import_session_id}`;
      const savedProgress = localStorage.getItem(sessionKey);

      if (savedProgress) {
        try {
          const progress = JSON.parse(savedProgress);

          this.completed_batch_ranges = progress.completed_batch_ranges || [];
          this.last_completed_batch = progress.last_completed_batch || -1;
          this.completed_batches = this.completed_batch_ranges.length;
          this.total_batches = progress.total_batches || 0;
          this.number_of_paths = progress.number_of_paths || 0;
          this.batch_size = progress.batch_size || 5;

          console.log(`🔄 Resuming import session ${this.import_session_id}`);
          console.log(
            `📊 Progress: ${this.completed_batches}/${this.total_batches} batches completed`,
          );
          console.log(
            `🚀 Starting from batch ${this.last_completed_batch + 2}/${this.total_batches}`,
          );

          return true;
        } catch (error) {
          console.error("Failed to parse saved import progress:", error);
          return false;
        }
      }

      return false;
    },

    /**
     * Clear completed import session from storage
     */
    async clearImportSession(sessionId?: string) {
      const idToClear = sessionId || this.import_session_id;
      if (idToClear) {
        const sessionKey = `cr_import_session_${idToClear}`;
        localStorage.removeItem(sessionKey);
        console.log(`🧹 Cleared import session: ${idToClear}`);
      }

      // Reset session state
      this.import_session_id = null;
      this.completed_batch_ranges = [];
      this.last_completed_batch = -1;
      this.current_import_settings = null;
    },

    /**
     * Get all incomplete import sessions for current project
     */
    getIncompleteImportSessions(
      projectId: number,
      companyId: number,
      officeId: number,
    ) {
      const allSessions = [];

      // Check localStorage for all import sessions
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("cr_import_session_")) {
          try {
            const sessionData = JSON.parse(localStorage.getItem(key) || "{}");

            // Filter for matching project and incomplete sessions
            if (
              sessionData.settings &&
              sessionData.settings.projectId === projectId &&
              sessionData.settings.companyId === companyId &&
              sessionData.settings.officeId === officeId &&
              sessionData.last_completed_batch < sessionData.total_batches - 1
            ) {
              allSessions.push({
                ...sessionData,
                remainingBatches:
                  sessionData.total_batches -
                  sessionData.completed_batch_ranges.length,
                progressPercentage: Math.round(
                  (sessionData.completed_batch_ranges.length /
                    sessionData.total_batches) *
                    100,
                ),
              });
            }
          } catch (error) {
            console.warn(`Invalid session data in ${key}:`, error);
          }
        }
      }

      // Sort by most recent first
      return allSessions.sort(
        (a, b) =>
          new Date(b.saved_at).getTime() - new Date(a.saved_at).getTime(),
      );
    },

    /**
     * Legacy method - kept for backward compatibility
     * @deprecated Use startBatchImport instead
     */
    async startImport(settings: ImportSettingsInput) {
      return this.startBatchImport(settings);
    },
    /**
     * REMOVED: Status polling no longer needed with frontend-driven batch processing
     * Progress is now calculated directly from completed batches
     * @deprecated This method is obsolete with the new batch processing approach
     */
    /**
     * Start Geocoding mit gegebenen Adressen
     * @param path
     */
    async startGeocoding(path: any) {
      try {
        console.log("Starting geocoding for path: ", path.path_id);
        // URL for the GET request
        const adress_id = path.origin_id || path.id;
        console.log("Adress_id: ", adress_id);

        // Fetch address data
        const addressData = await this.getAddressData(adress_id);
        // console.log("Address data for geocoding: ", addressData);

        // Check if the 'geo' column is empty
        if (
          addressData.geo &&
          addressData.geo.coordinates &&
          addressData.geo.coordinates.length === 2
        ) {
          console.log(
            `Geocoding not needed for address_id: ${adress_id}, geo data already exists`,
          );
          return {
            success: true,
            adress_id: adress_id,
            message: "Geocoding not needed, geo data already exists",
          };
        }

        // If the geo data is empty, we proceed with geocoding
        const geocoding_url = `${GEOCODING_URL}?adress_id=${adress_id}`;
        //console.log("GeoCoding URL: ", geocoding_url);

        // API call to start geocoding
        const geoResponse = await JmApi.get(geocoding_url);
        // console.log("GeoCoding Response: ", geoResponse);

        // Check if the call was successful
        if (geoResponse.status !== "success") {
          // If geocoding failed, return the error information
          console.log("Geocoding failed for address_id:", adress_id);
          return {
            success: false,
            error: geoResponse.error || "Geocoding failed",
            adress_id: adress_id,
          };
        } else {
          // Extract geocoding data from the response
          const geoData = geoResponse.data.data[0].lonLat;
          // Write geocoding data to the jm-database
          await this.putAdressGeoData(geoData, path);
          return {
            success: true,
            adress_id: adress_id,
          };
        }
      } catch (error) {
        console.error("Error starting geocoding:", JSON.stringify(error));
        return {
          success: false,
          error: "Unexpected error during geocoding",
          adress_id: path.origin_id || path.id,
        };
      }
    },

    /**
     * Post geoData to jm-database in adress table
     * @param geoData
     * @param path
     */
    async putAdressGeoData(geoData: any, pathOrAdressId: any) {
      try {
        let id;
        if (typeof pathOrAdressId === "number") {
          id = pathOrAdressId;
        } else if (pathOrAdressId.origin_id) {
          id = pathOrAdressId.origin_id;
        } else if (pathOrAdressId.id) {
          id = pathOrAdressId.id;
        } else {
          throw new Error("No valid id found in pathOrAdressId object");
        }
        const put_body = {
          id,
          geo: { coordinates: geoData, type: "Point" },
        };
        const response = await JmApi.put(ADRESS_URL, put_body);
        // console.log("Response from putAdressGeoData: ", response);
      } catch (error) {
        console.error(
          "Fehler beim Senden der Geodaten:",
          JSON.stringify(error),
        );
      }
    },

    async putAdressFailedGeocoding(adressData: any, pathOrAdressId: any) {
      console.log("AdressData Stadt: ", adressData.district);

      try {
        let id;
        if (typeof pathOrAdressId === "number") {
          id = pathOrAdressId;
        } else {
          id = Number(pathOrAdressId.id);
        }
        const put_body = {
          id,
          street: adressData.street,
          house_number: adressData.house_number || "",
          postal_code: adressData.postal_code,
          district: adressData.district,
        };
        const response = await JmApi.put(ADRESS_URL, put_body);
        // console.log("Response from putAdressFailedGeocoding: ", response);
        return response; // return the updated address data
      } catch (error) {
        console.error(
          "Fehler beim Updaten der Adressdaten:",
          JSON.stringify(error),
        );
      }
    },

    // function that calls putAdressFailedGeocoding and assigns the returned data to failedGeoPaths
    async updateFailedGeoPaths(adressData: any, pathOrAdressId: any) {
      try {
        // Update only the specific address, not all failed paths
        const updatedAddress = await this.putAdressFailedGeocoding(
          adressData,
          pathOrAdressId,
        );

        // Update the specific path in the failedGeoPaths array
        const pathIndex = this.failedGeoPaths.findIndex(
          (path) => path.id === pathOrAdressId,
        );
        if (pathIndex !== -1) {
          this.failedGeoPaths[pathIndex] = {
            ...this.failedGeoPaths[pathIndex],
            adressData: updatedAddress,
          };
        }

        console.log("Updated specific address for path:", pathOrAdressId);
        return updatedAddress;
      } catch (error) {
        console.error(
          "Fehler beim Aktualisieren der fehlgeschlagenen Geocodings:",
          JSON.stringify(error),
        );
      }
    },

    async getDestinationAddressId(office_id: number) {
      try {
        const response = await JmApi.get(
          `${OFFICE_ADRESS_ID_URL}?officeId=${office_id}`,
        );
        return response;
      } catch (error) {
        console.error(
          "Fehler beim Abrufen der Adress-ID:",
          JSON.stringify(error),
        );
      }
    },

    async geocodeDestination(adress_id: number) {
      try {
        // URL für den GET-Aufruf vorbereiten
        const geocoding_url = `${GEOCODING_URL}?adress_id=${adress_id}`;

        // API-Aufruf zum Starten des Geocodings
        const geoResponse = await JmApi.get(geocoding_url);
        // Überprüfung, ob der Aufruf erfolgreich war
        if (geoResponse.status !== "success") {
          throw new Error("Failed to trigger geocoding");
        } else {
          // Geocoding-Daten aus der Response extrahieren
          const geoData = geoResponse.data.data[0].lonLat;
          // Geocoding-Daten in die jm-database schreiben
          await this.putAdressGeoData(geoData, adress_id);
        }
      } catch (error) {
        console.error(
          "Fehler beim Starten des Geocodings:",
          JSON.stringify(error),
        );
      }
    },

    async fetchParameters() {
      try {
        const response = await JmApi.get(PARAMETERS_URL);
        this.parameters = response;

        return this.parameters;
      } catch (error) {
        console.error(
          "Fehler beim Abrufen der Parameter:",
          JSON.stringify(error),
        );
      }
    },

    async saveParameters(parameters: any) {
      console.log("Parameters to save: ", parameters);

      try {
        JmApi.put(PARAMETERS_URL, parameters)
          .then((_) => {
            this.fetchParameters();
            this.parametersUpdated = true;
          })
          .catch((data) => {
            throw new Error(data.message);
          });
      } catch (error) {
        console.error(
          "Fehler beim Speichern der Parameter:",
          JSON.stringify(error),
        );
      }
    },

    async fetchDefaultParameters() {
      try {
        const response = await JmApi.get(DEFAULT_PARAMETERS_URL);
        this.defaultParameters = response;
        return this.defaultParameters;
      } catch (error) {
        console.error(
          "Fehler beim Abrufen der Standardparameter:",
          JSON.stringify(error),
        );
      }
    },

    async createExportDownload() {
      try {
        // Get the first and last origin_id from the uniqueAddresses array
        // Sort the uniqueAddresses array by origin_id from lowest to highest
        this.uniqueAddresses.sort((a, b) => a.origin_id - b.origin_id);

        const firstId = this.uniqueAddresses[0].origin_id;
        const lastId =
          this.uniqueAddresses[this.uniqueAddresses.length - 1].origin_id;
        // const officeId = this.selectedOfficeId;

        const response = await JmApi.get_download(
          `${DOWNLOAD_CSV_URL}?originIdFirst=${firstId}&originIdLast=${lastId}`,
          true,
        );
        return response;
      } catch (error) {
        console.error(
          "Fehler beim Erstellen des Downloads:",
          JSON.stringify(error),
        );
      }
    },

    async downloadTableAsExcel(tableData: any) {
      try {
        console.log("Preparing to download table as Excel");

        // Make the API request using JmApi
        const blob = await JmApi.post_download(DOWNLOAD_EXCEL_URL, tableData);

        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `vergleichsmatrix-${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        return true;
      } catch (error) {
        console.error("Error downloading table as Excel:", error);
        throw error;
      }
    },

    async fetchCleverRouteExport() {
      try {
        const response = await JmApi.get_download(DOWNLOAD_CSV_URL);
        return response;
      } catch (error) {
        console.error(
          "Fehler beim Abrufen des CleverRoute-Exports:",
          JSON.stringify(error),
        );
      }
    },

    async fetchCleverRouteResults() {
      try {
        const response = await JmApi.get(RESULTS_URL);
        return response;
      } catch (error) {
        console.error(
          "Fehler beim Abrufen der Ergebnisse:",
          JSON.stringify(error),
        );
      }
    },

    async abort_import(projectId?: number, officeId?: number) {
      try {
        console.log("Sending abort request to backend");
        this.abort_requested = true;
        this.is_batch_importing = false;
        const params = new URLSearchParams();
        if (this.import_session_id) {
          params.set("importSessionId", this.import_session_id);
        }
        if (projectId) {
          params.set("project_id", String(projectId));
        }
        if (officeId) {
          params.set("office_id", String(officeId));
        }
        const url = params.toString() ? `${CRIMPORT_URL}?${params.toString()}` : CRIMPORT_URL;
        const response = await JmApi.delete(url);
        this.last_abort_summary = response;
        console.log("Successfully sent abort request to backend");
        return response;
      } catch (error) {
        console.error("Failed to abort import", JSON.stringify(error));
        throw error;
      }
    },

    /**
     * Alte Import Funktionen, die nicht in Verwendung sind.
     */
    getSelectedImport(): CleverRouteImport {
      if (this.getSelectedImportId === undefined) {
        throw new Error(
          `Error occured, when trying to get selected import. No import selected`,
        );
      }
      try {
        // @ts-ignore
        return this.getImportFromId(this.selectedImportId);
      } catch (e) {
        throw new Error(
          `Error occured, when trying to get selected import. Invalid id: ${
            this.getSelectedImportId
          } ${JSON.stringify(e)}`,
        );
      }
    },

    initializeStopping(id: number) {
      try {
        this.idToIndex(id);
      } catch (e) {
        throw new Error(
          `Error when trying to initialize stopping: ${JSON.stringify(e)}`,
        );
      }
      this.selectedImportId = id;
      this.stoppingStatus = StoppingStatus.Pending;
    },

    abortStopping() {
      // We will leave it as is, because, eventho the functions do the same, the intent and the level of abstraction is different.
      // Can be augmented.
      this.resetToBeforeStopping();
    },

    async postImport(crImport: CleverRouteImport) {
      const isValid: boolean = this.validateImport(crImport);
      if (!isValid) {
        throw new Error(`Given import ${crImport} is not valid.`);
      }
      // post import (for now just state but later will be through the jm-api)
      crImport.id = this.getImports.length + 1;
      this.imports.push(crImport);
    },

    async postImportHandler(crImport: CleverRouteImport) {
      try {
        await this.postImport(crImport);
      } catch (e) {
        console.error(
          `Error occured when trying to post import ${JSON.stringify(e)}`,
        );
      }
    },

    stopImportHandler(deleteData: boolean) {
      try {
        this.stopImport(deleteData);
      } catch (e) {
        console.error(
          `Error occured when trying to stop import: ${JSON.stringify(e)}`,
        );
      }
    },

    stopImport(deleteData: boolean) {
      const id = this.selectedImportId as number;
      // get index of belonging import in state
      const index = this.idToIndex(id);
      // delete import and validate api response (for now only state...)
      this.imports.splice(index, 1);
      // reset to state before stopping
      this.resetToBeforeStopping();
    },

    resetToBeforeStopping() {
      this.selectedImportId = undefined;
      this.stoppingStatus = StoppingStatus.None;
    },

    async updateImportStatusAndErrorsHandler(id: number) {
      try {
        await this.updateImportStatusAndErrors(id);
      } catch (e) {
        console.error(
          `Error occured, when trying to update Import with id ${id}: ${JSON.stringify(
            e,
          )}`,
        );
      }
    },

    async updateImportStatusAndErrors(id: number) {
      // make api request for updating the import...
      // store the response in state
      const stubStatus = 0.4;
      const stubError: ImportError = {
        originAdress: {
          street: "Test Street",
          house_number: "Test House_number",
          district: "Test district",
          postal_code: "Test postalcode",
          geo: {
            coordinates: [51.12015, 7.105105105],
            type: "Point",
          },
          id: 100,
        },
        message: "Test Error!",
      };
      const index = this.idToIndex(id);
      this.imports[index].status = stubStatus;
      this.imports[index].errors = [stubError];
    },

    getImportSettingsHandler(id: number) {
      try {
        this.getImportSettings(id);
      } catch (e) {
        console.error(
          `An error occured when trying to get the status of an import under id ${id}: ${JSON.stringify(
            e,
          )}`,
        );
      }
    },
    getImportSettings(id: number) {
      const index = this.idToIndex(id);
      return this.imports[index].settings;
    },
    validateImport(crImport: CleverRouteImport): boolean {
      // validation of the import
      if (typeof crImport.status === "number" && crImport.status > 0) {
        console.warn(
          "new Import appended, which has already made progress before posting.",
        );
      }
      if (crImport.settings.transports.length === 0) {
        console.error("Tried to post an import with no transports.");
        return false;
      }
      if (crImport.errors.length > 0) {
        console.warn("Errors contained before posting new Import!");
        return false;
      }
      if (crImport.id) {
        console.warn("Import with id posted, checking if id is taken.");
        // send get request to api...
      }
      return true;
    },
    async getImportErrors(id: number) {
      // check if id exists within running imports
      const indexImportToUpdate: number | null = this.idToIndex(id);
      if (!indexImportToUpdate) {
        return;
      }
      // make api request for updating the import...
      // For now only dummy return for testing.
      const dummyErrors: ImportError[] = [
        {
          originAdress: {
            street: "dummy street",
            house_number: "dummy house number",
            postal_code: "dummy postal",
            district: "dummy district",
            geo: {
              coordinates: [0, 0],
              type: "Point",
            },
            id: 0,
          },
          message: "dummy message",
        },
      ];
      return dummyErrors;
    },
    getImportFromId(id: number) {
      try {
        const index = this.idToIndex(id);
        return this.imports[index];
      } catch (e) {
        console.error(e);
      }
    },
    idToIndex(id: number): number {
      const indexOfSelectedImport: number = this.imports.findIndex((a) => {
        if (a.id === undefined) {
          return false;
        }
        return a.id === id;
      });
      if (indexOfSelectedImport === -1) {
        throw new Error(`No import found with id: ${id}`);
      }
      return indexOfSelectedImport;
    },
    async saveMobscoreSettings(officeId: number, mobscoreMetrics: any) {
      try {
        const settings = {
          // Convert string values to numbers using Number()
          weight_time: Number(
            mobscoreMetrics.find((m: any) => m.name === "Zeit").value,
          ),
          weight_sport: Number(
            mobscoreMetrics.find((m: any) => m.name === "Bewegung").value,
          ),
          weight_co2: Number(
            mobscoreMetrics.find((m: any) => m.name === "CO₂").value,
          ),
          weight_cost: Number(
            mobscoreMetrics.find((m: any) => m.name === "Kosten").value,
          ),
          budget_time: 60,
          budget_sport: 10000,
          budget_co2: 1000,
          budget_cost: 10,
        };

        console.log("Request body:", settings);
        const response = await JmApi.put(
          `${MOBSCORE_URL}?OfficeId=${officeId}`,
          settings,
        );
        console.log("Success response:", response);
        return true;
      } catch (error) {
        console.error(
          "Fehler beim Speichern der Mobscore-Einstellungen:",
          error,
        );
        throw error;
      }
    },
    // New office parameter actions (designed to replace global parameter methods)
    /**
     * Fetch parameters for a specific office (will replace fetchParameters)
     * Falls back to defaults if no office-specific parameters exist
     * @param {number} officeId - ID of the office
     * @returns {Promise<OfficeParameter[]>} Array of parameter objects
     */
    async fetchOfficeParameters(officeId: number): Promise<OfficeParameter[]> {
      if (!officeId) {
        this.parametersError = "No office ID provided";
        return [];
      }
      this.parametersLoading = true;
      this.parametersError = null;
      this.currentOfficeId = officeId;
      try {
        const url = `${OFFICE_PARAMETERS_URL}?officeId=${officeId}`;

        const response = await JmApi.get(url);
        const data = response as OfficeParameter[];
        this.officeParameters = data;

        // Check if these are office-specific or defaults
        // Office-specific parameters will have officeId field
        this.parametersInitialized =
          data.length > 0 &&
          (data[0].officeId === officeId || data[0].office_id === officeId);

        return data;
      } catch (error) {
        console.error(
          `❌ Error fetching office parameters for office ${officeId}:`,
          error,
        );

        // Check if it's an API error with specific message
        if (error && typeof error === "object" && "message" in error) {
          this.parametersError = (error as any).message;
          console.error(`API Error Message: ${(error as any).message}`);
        } else {
          this.parametersError =
            error instanceof Error ? error.message : "Unknown error";
        }
        this.officeParameters = [];
        this.parametersInitialized = false;
        this.currentOfficeId = null;
        throw error;
      } finally {
        this.parametersLoading = false;
      }
    },
    /**
     * Save office-specific parameters (will replace saveParameters)
     * Creates new or updates existing parameters
     * @param {number} officeId - ID of the office
     * @param {OfficeParameter[]} parameters - Array of parameter objects to save
     * @returns {Promise<any>} Response with success status and saved parameters
     */
    async saveOfficeParameters(
      officeId: number,
      parameters: OfficeParameter[],
    ): Promise<any> {
      if (!officeId) {
        throw new Error("Cannot save parameters: no office ID provided");
      }
      if (!Array.isArray(parameters)) {
        throw new Error("Parameters must be an array");
      }
      if (parameters.length === 0) {
        throw new Error("No parameters to save");
      }
      this.parametersLoading = true;
      this.parametersError = null;
      this.currentOfficeId = officeId;
      try {
        // Backend expects individual parameter objects, not an array
        // Send each parameter separately
        const results = [];
        for (const param of parameters) {
          const parameterWithOfficeId = {
            ...param,
            // office_id is already included in param from frontend
          };
          // Use POST for new parameters (id is null) and PUT for existing ones
          if (param.id === null || param.id === undefined) {
            const result = await JmApi.post(
              OFFICE_PARAMETERS_URL,
              parameterWithOfficeId,
            );
            results.push(result);
          } else {
            const result = await JmApi.put(
              OFFICE_PARAMETERS_URL,
              parameterWithOfficeId,
            );
            results.push(result);
          }
        }
        // Update local state with saved parameters
        this.officeParameters = results.length > 0 ? results : parameters;
        this.parametersInitialized = true;
        this.parametersUpdated = true; // Signal to navigation that parameters are saved
        return results;
      } catch (error) {
        console.error(
          `❌ Error saving office parameters for office ${officeId}:`,
          error,
        );

        // Log detailed error information for debugging
        if (error && typeof error === "object") {
          console.error(`🔍 Error details:`, {
            message: (error as any).message,
            status: (error as any).status,
            code: (error as any).code,
            detail: (error as any).detail,
            fullError: error,
          });
        }
        this.parametersError =
          error instanceof Error ? error.message : "Unknown error";
        throw error;
      } finally {
        this.parametersLoading = false;
      }
    },
    /**
     * Initialize office parameters from defaults
     * Only needed if office has no parameters yet
     * @param {number} officeId - ID of the office
     * @returns {Promise<any>} Response with initialized parameters
     */
    async initializeOfficeParameters(officeId: number): Promise<any> {
      if (!officeId) {
        throw new Error("Cannot initialize parameters: no office ID provided");
      }
      this.parametersLoading = true;
      this.parametersError = null;
      this.currentOfficeId = officeId;
      try {
        const result = await JmApi.post(
          `${OFFICE_PARAMETERS_INITIALIZE_URL}/${officeId}/initialize`,
          {},
        );
        // Update local state with initialized parameters
        this.officeParameters = result.parameters || [];
        this.parametersInitialized = true;
        console.log(
          `✓ Initialized ${this.officeParameters.length} parameters for office ${officeId}`,
        );
        return result;
      } catch (error) {
        console.error("Error initializing office parameters:", error);
        this.parametersError =
          error instanceof Error ? error.message : "Unknown error";
        throw error;
      } finally {
        this.parametersLoading = false;
      }
    },
    /**
     * Delete a specific parameter for an office
     * System will fallback to defaults after deletion
     * @param {number} officeId - ID of the office
     * @param {string} transportType - Type of transport to delete
     * @returns {Promise<boolean>} Success status
     */
    async deleteOfficeParameter(
      officeId: number,
      transportType: string,
    ): Promise<boolean> {
      if (!officeId || !transportType) {
        throw new Error("Office ID and transport type are required");
      }
      this.parametersError = null;
      try {
        await JmApi.delete(
          `${OFFICE_PARAMETERS_DELETE_URL}/${officeId}/transport/${transportType}`,
        );
        // Remove from local state
        this.officeParameters = this.officeParameters.filter(
          (p) => (p.transportType || p.transport_type) !== transportType,
        );
        console.log(
          `✓ Deleted parameter for ${transportType} from office ${officeId}`,
        );
        return true;
      } catch (error) {
        console.error("Error deleting office parameter:", error);
        this.parametersError =
          error instanceof Error ? error.message : "Unknown error";
        throw error;
      }
    },
    /**
     * Reset parameters state (enhanced version for office context)
     * Call when changing offices or resetting form
     */
    resetOfficeParameters(): void {
      this.officeParameters = [];
      this.parametersLoading = false;
      this.parametersInitialized = false;
      this.parametersError = null;
      this.currentOfficeId = null;
      console.log("✓ Office parameters state reset");
    },
  },
});
